import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, tap } from 'rxjs';
import { environment } from '../environments/environment';
import type { PartnerAgreementResponse, UserProfileResponse as UserProfile } from '@kin/property-tax-types';

export type { UserProfile };

@Injectable({ providedIn: 'root' })
export class UserProfileService {
  private http = inject(HttpClient);

  private _profile: UserProfile | null = null;

  get profile(): UserProfile | null {
    return this._profile;
  }

  clear(): void {
    this._profile = null;
  }

  loadProfile(): Observable<UserProfile | null> {
    if (this._profile) return of(this._profile);
    return this.http.get<UserProfile>(`${environment.apiUrl}/user/profile`).pipe(
      tap(profile => (this._profile = profile)),
    );
  }

  /** Drops the cached profile and re-reads it from the server. Used by `profileRefreshGuard`. */
  refreshProfile(): Observable<UserProfile | null> {
    this._profile = null;
    return this.loadProfile();
  }

  /**
   * Records acceptance of the FL partner-handoff terms. User-level, not case-level: FL users
   * never get a `Case` row. The backend write is first-wins, so a repeat call is a no-op.
   * Patches `partnerAgreedAt` onto the cache so a return to `/partner` still shows it checked.
   */
  savePartnerAgreement(): Observable<PartnerAgreementResponse> {
    return this.http.post<PartnerAgreementResponse>(`${environment.apiUrl}/user/partner-agreement`, {}).pipe(
      tap(res => {
        if (this._profile) this._profile = { ...this._profile, partnerAgreedAt: res.agreedAt };
      }),
    );
  }
}
